import { Box, Typography, Avatar } from '@mui/material';
import { useState } from 'react';
import type { appRevenueTop5Item } from '../../types/pointhome.types';

type Props = { data: appRevenueTop5Item[] };

const krw = (n: number) =>
  new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW', maximumFractionDigits: 0 }).format(n);

const GRID5 = {
  display: 'grid',
  gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', sm: 'repeat(3,1fr)', md: 'repeat(5,1fr)' },
  gap: { xs: 1, sm: 1.5 },
} as const;

function Pill({ item, rank }: { item: appRevenueTop5Item; rank: number }) {
  const medal = (() => {
    if (rank === 1) return { bg: '#FFD54F', fg: '#263238', border: undefined, shadow: '0 0 0 1px rgba(0,0,0,0.04)' };
    if (rank === 2) return { bg: '#C0C0C0', fg: '#263238', border: undefined, shadow: '0 0 0 1px rgba(0,0,0,0.05)' }; // 진한 실버
    if (rank === 3) return { bg: '#CD7F32', fg: '#FFFFFF', border: undefined, shadow: '0 0 0 1px rgba(0,0,0,0.06)' }; // 브론즈
    return { bg: 'transparent', fg: '#607D8B', border: '1.5px solid #CFD8DC', shadow: 'none' }; // 비메달권
  })();

  const [imageError, setImageError] = useState(false);
  const appImagePath = `/img/adpopcorn/app/${item.appName}.png`;

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 1.25 },
        p: { xs: 1, sm: 1.25 },
        borderRadius: 9999,
        border: '1px solid #E0E3E7',
        bgcolor: '#fff',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Avatar
        src={!imageError ? appImagePath : undefined}
        onError={handleImageError}
        sx={{
          width: { xs: 28, sm: 36, md: 40 },
          height: { xs: 28, sm: 36, md: 40 },
          bgcolor: medal.bg,
          color: medal.fg,
          border: medal.border,
          boxShadow: medal.shadow,
          fontWeight: 800,
          fontSize: { xs: 12, sm: 14 },
          flexShrink: 0,
        }}
      >
        {rank}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="subtitle2" noWrap title={item.appName}>
          {item.appName}
        </Typography>
        <Typography variant="subtitle2" fontWeight={700}>
          {krw(item.revenue)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function PointhomeAppRevenueTop10({ data }: Props) {
  const items = (data ?? []).slice(0, 10);

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={GRID5}>
        {items.length === 0 ? (
          <Box
            sx={{
              gridColumn: '1 / -1',
              p: 1.25,
              borderRadius: 1,
              border: '1px dashed #E0E3E7',
              bgcolor: '#FAFAFA',
            }}
            aria-live="polite"
          >
            <Typography variant="body2" color="text.secondary">
              표시할 앱이 없습니다.
            </Typography>
          </Box>
        ) : (
          items.map((it, idx) => <Pill key={`${it.appName}-${idx}`} item={it} rank={idx + 1} />)
        )}
      </Box>
    </Box>
  );
}
